import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Truck, 
  MapPin, 
  Users, 
  LogOut, 
  RefreshCw,
  Clock,
  Navigation,
  Phone,
  Calendar,
  Route,
  ChevronRight,
  FileText,
  Radio,
  Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import { useWebSocketLocation } from '../../hooks/useWebSocketLocation';
import { ClientReportsTab } from './ClientReportsTab';
import { ClientAccountingTab } from './ClientAccountingTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [activeTab, setActiveTab] = useState('location');
  const [tripHistory, setTripHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [useWebSocket, setUseWebSocket] = useState(true);

  // WebSocket for real-time updates
  const {
    isConnected: wsConnected,
    drivers: wsDrivers,
    lastUpdate: wsLastUpdate,
    refresh: wsRefresh,
    error: wsError
  } = useWebSocketLocation('client');

  // Fallback HTTP state
  const [httpDrivers, setHttpDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Transform WebSocket drivers to match HTTP format
  const transformedWsDrivers = wsDrivers.map(d => ({
    id: d.driver_id,
    first_name: d.driver_name?.split(' ')[0] || 'Chauffeur',
    last_name: d.driver_name?.split(' ').slice(1).join(' ') || '',
    phone: d.phone,
    is_online: d.timestamp ? new Date(d.timestamp) > new Date(Date.now() - 5 * 60 * 1000) : false,
    location: {
      latitude: d.latitude,
      longitude: d.longitude,
      speed: d.speed,
      heading: d.heading,
      timestamp: d.timestamp
    }
  }));

  // Use WebSocket drivers if connected, otherwise HTTP
  const drivers = useWebSocket && wsConnected ? transformedWsDrivers : httpDrivers;

  useEffect(() => {
    const token = localStorage.getItem('client_portal_token');
    const userData = localStorage.getItem('client_portal_user');
    
    if (!token || !userData) {
      navigate('/client-portal/login');
      return;
    }
    
    setUser(JSON.parse(userData));
  }, [navigate]);

  const fetchDrivers = useCallback(async () => {
    const token = localStorage.getItem('client_portal_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/saas/portal/my-drivers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('client_portal_token');
        localStorage.removeItem('client_portal_user');
        navigate('/client-portal/login');
        return;
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        setHttpDrivers(data);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Fallback to HTTP polling if WebSocket not connected
  useEffect(() => {
    if (!useWebSocket || !wsConnected) {
      fetchDrivers();
      const interval = setInterval(fetchDrivers, 60000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [useWebSocket, wsConnected, fetchDrivers]);

  const fetchTripHistory = useCallback(async () => {
    const token = localStorage.getItem('client_portal_token');
    if (!token) return;

    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/saas/portal/trip-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTripHistory(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching trip history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history' && user?.permissions?.includes('view_trip_history')) {
      fetchTripHistory();
    }
  }, [activeTab, user, fetchTripHistory]);

  const handleLogout = () => {
    localStorage.removeItem('client_portal_token');
    localStorage.removeItem('client_portal_user');
    toast.success('Déconnexion réussie');
    navigate('/client-portal/login');
  };

  const canViewLocation = user?.permissions?.includes('view_location');
  const canViewHistory = user?.permissions?.includes('view_trip_history');
  const canViewReports = user?.permissions?.includes('view_reports');
  const canViewAccounting = user?.permissions?.includes('view_accounting');
  const hasDriverModules = canViewLocation || canViewHistory || canViewReports;
  const tabs = [
    canViewLocation && { id: 'location', label: 'Localisation en direct', icon: MapPin },
    canViewHistory && { id: 'history', label: 'Historique des trajets', icon: Calendar },
    canViewReports && { id: 'reports', label: 'Rapports journaliers', icon: FileText },
    canViewAccounting && { id: 'accounting', label: 'Comptabilité', icon: Calculator },
  ].filter(Boolean);
  if (user && tabs.length && !tabs.some(t => t.id === activeTab)) setActiveTab(tabs[0].id);

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-slate-900 ring-1 ring-amber-500/30">
              <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-white font-bold">ProPilote</h1>
              <p className="text-xs text-slate-400">{user.organization_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium">{user.client_name}</p>
              <p className="text-slate-400 text-xs">{user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
              title="Déconnexion"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {hasDriverModules && (<>
        {/* WebSocket Status Bar */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {useWebSocket && wsConnected ? (
              <span className="flex items-center gap-2 text-green-400 text-sm bg-green-500/10 px-3 py-1.5 rounded-full">
                <Radio size={14} className="animate-pulse" />
                Temps réel actif
              </span>
            ) : (
              <span className="text-slate-500 text-sm">Actualisation: 1 min</span>
            )}
          </div>
          <button
            onClick={() => setUseWebSocket(!useWebSocket)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              useWebSocket 
                ? 'bg-green-600/20 text-green-400 border border-green-600/50' 
                : 'bg-slate-800 text-slate-400'
            }`}
          >
            <Radio size={14} />
            {useWebSocket ? 'Temps réel' : 'Polling'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <Users size={18} />
              <span className="text-sm">Chauffeurs</span>
            </div>
            <p className="text-2xl font-bold text-white">{drivers.length}</p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm">En ligne</span>
            </div>
            <p className="text-2xl font-bold text-green-500">
              {drivers.filter(d => d.is_online).length}
            </p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-400 mb-1">
              <div className="w-3 h-3 bg-slate-500 rounded-full" />
              <span className="text-sm">Hors ligne</span>
            </div>
            <p className="text-2xl font-bold text-slate-400">
              {drivers.filter(d => !d.is_online).length}
            </p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <button
              onClick={() => {
                if (useWebSocket && wsConnected) {
                  wsRefresh();
                } else {
                  fetchDrivers();
                }
                if (activeTab === 'history') fetchTripHistory();
              }}
              disabled={loading}
              className="w-full h-full flex items-center justify-center gap-2 text-amber-500 hover:text-amber-400"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="text-sm font-medium">Actualiser</span>
            </button>
          </div>
        </div>
        </>)}

        {/* Tabs */}
        {tabs.length > 0 && (
          <div className="flex border-b border-slate-800 mb-6 overflow-x-auto">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`client-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === t.id
                    ? 'border-amber-500 text-amber-500'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <t.icon size={18} />
                {t.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === 'reports' && canViewReports && <ClientReportsTab />}
        {activeTab === 'accounting' && canViewAccounting && <ClientAccountingTab />}

        {/* Location Tab */}
        {activeTab === 'location' && canViewLocation && (
          <>
            {/* Drivers List */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Users size={20} />
                Mes chauffeurs
              </h2>
              
              {drivers.length === 0 ? (
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
                  <Users className="mx-auto text-slate-600 mb-4" size={48} />
                  <h3 className="text-white font-medium mb-2">Aucun chauffeur assigné</h3>
                  <p className="text-slate-400 text-sm">
                    Contactez votre administrateur pour accéder aux chauffeurs
                  </p>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {drivers.map(driver => (
                    <div 
                      key={driver.id}
                      className={`bg-slate-900 border rounded-xl p-4 cursor-pointer transition-all ${
                        selectedDriver?.id === driver.id 
                          ? 'border-amber-500' 
                          : 'border-slate-800 hover:border-slate-700'
                      }`}
                      onClick={() => setSelectedDriver(driver)}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                          driver.is_online 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-slate-800 text-slate-500'
                        }`}>
                          {driver.first_name?.[0] || 'C'}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-medium">
                            {driver.first_name} {driver.last_name}
                          </h3>
                          <p className="text-slate-400 text-sm flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${
                              driver.is_online ? 'bg-green-500' : 'bg-slate-500'
                            }`} />
                            {driver.is_online ? 'En ligne' : 'Hors ligne'}
                          </p>
                        </div>
                      </div>

                      {driver.phone && (
                        <a 
                          href={`tel:${driver.phone}`}
                          className="flex items-center gap-2 text-slate-400 hover:text-amber-500 text-sm mb-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone size={14} />
                          {driver.phone}
                        </a>
                      )}

                      {driver.location && (
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin size={12} />
                          <span>
                            {driver.location.latitude?.toFixed(4)}, {driver.location.longitude?.toFixed(4)}
                          </span>
                          {driver.location.speed != null && (
                            <span className="ml-2 flex items-center gap-1">
                              <Navigation size={12} />
                              {Number(driver.location.speed).toFixed(0)} km/h
                            </span>
                          )}
                        </div>
                      )}

                      {driver.location?.timestamp && (
                        <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
                          <Clock size={12} />
                          {new Date(driver.location.timestamp).toLocaleString('fr-CA')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Map */}
            {drivers.some(d => d.location) && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <MapPin size={20} />
                    Carte en direct
                  </h2>
                </div>
                <ClientMap 
                  drivers={drivers.filter(d => d.location)} 
                  selectedDriver={selectedDriver}
                  onSelectDriver={setSelectedDriver}
                />
              </div>
            )}
          </>
        )}

        {/* History Tab */}
        {activeTab === 'history' && canViewHistory && (
          <div>
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Calendar size={20} />
              Historique des trajets
            </h2>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : tripHistory.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
                <Route className="mx-auto text-slate-600 mb-4" size={48} />
                <h3 className="text-white font-medium mb-2">Aucun trajet enregistré</h3>
                <p className="text-slate-400 text-sm">
                  L'historique des trajets de vos chauffeurs apparaîtra ici
                </p>
                <p className="text-slate-500 text-xs mt-4">
                  Les trajets sont enregistrés automatiquement lorsque les chauffeurs sont en service
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {tripHistory.map((trip, index) => (
                  <div 
                    key={trip.id || index}
                    className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <Route className="text-amber-500" size={20} />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{trip.driver_name}</h3>
                          <p className="text-slate-400 text-sm">
                            {new Date(trip.date).toLocaleDateString('fr-CA', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="text-slate-500" size={20} />
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-slate-500 text-xs">Distance</p>
                        <p className="text-white font-medium">{trip.distance_km?.toFixed(1) || '—'} km</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Durée</p>
                        <p className="text-white font-medium">{trip.duration_hours?.toFixed(1) || '—'} h</p>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs">Arrêts</p>
                        <p className="text-white font-medium">{trip.stops_count || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Map Component for Client Portal
const ClientMap = ({ drivers, selectedDriver, onSelectDriver }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    const initMap = () => {
      if (window.google && window.google.maps && mapContainerRef.current && !mapRef.current) {
        mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
          center: { lat: 45.5017, lng: -73.5673 },
          zoom: 10,
          styles: [
            { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
          ],
          disableDefaultUI: true,
          zoomControl: true,
        });
        setMapLoaded(true);
      }
    };

    if (!window.google || !window.google.maps) {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
        script.async = true;
        script.onload = initMap;
        document.head.appendChild(script);
      } else {
        existingScript.addEventListener('load', initMap);
        setTimeout(initMap, 100);
      }
    } else {
      initMap();
    }
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    // Clear markers
    Object.values(markersRef.current).forEach(m => {
      try { m?.setMap?.(null); } catch(e) {}
    });
    markersRef.current = {};

    if (!drivers || drivers.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPositions = false;

    // SVG paths for markers
    const MARKER_PATHS = {
      circle: 'M 0,-8 A 8,8 0 1,1 0,8 A 8,8 0 1,1 0,-8 Z',
      square: 'M -7,-7 L 7,-7 L 7,7 L -7,7 Z',
      arrow: 'M 0,-12 L 8,8 L 0,4 L -8,8 Z'
    };

    drivers.forEach(driver => {
      if (!driver.location?.latitude || !driver.location?.longitude) return;
      
      hasPositions = true;
      const pos = { lat: Number(driver.location.latitude), lng: Number(driver.location.longitude) };
      bounds.extend(pos);

      // Get speed and heading
      const speed = driver.location.speed != null ? Number(driver.location.speed) : 0;
      const heading = driver.location.heading != null ? Number(driver.location.heading) : 0;
      const baseColor = driver.is_online ? '#22c55e' : '#64748b';

      // Determine marker based on speed
      let path, rotation;
      if (speed < 2) {
        path = MARKER_PATHS.circle;
        rotation = 0;
      } else if (speed < 10) {
        path = MARKER_PATHS.square;
        rotation = heading;
      } else {
        path = MARKER_PATHS.arrow;
        rotation = heading;
      }

      const marker = new window.google.maps.Marker({
        position: pos,
        map: mapRef.current,
        title: `${driver.first_name} ${driver.last_name}`,
        icon: {
          path: path,
          scale: 1,
          fillColor: baseColor,
          fillOpacity: 1,
          strokeColor: selectedDriver?.id === driver.id ? '#f59e0b' : '#fff',
          strokeWeight: selectedDriver?.id === driver.id ? 3 : 2,
          rotation: rotation,
          anchor: { x: 0, y: 0 }
        }
      });

      const movementStatus = speed < 2 ? 'À l\'arrêt' : speed < 10 ? 'Lent' : 'En mouvement';
      const infoWindow = new window.google.maps.InfoWindow({
        content: `<div style="padding:8px;color:#000;">
          <strong>${driver.first_name} ${driver.last_name}</strong>
          <p style="margin:4px 0;font-size:12px;">${driver.is_online ? '🟢 En ligne' : '⚫ Hors ligne'}</p>
          <p style="margin:0;font-size:12px;">${movementStatus}${speed > 0 ? ` • ${speed.toFixed(0)} km/h` : ''}</p>
        </div>`
      });

      marker.addListener('click', () => {
        infoWindow.open(mapRef.current, marker);
        onSelectDriver(driver);
      });

      markersRef.current[driver.id] = marker;
    });

    if (hasPositions) {
      try {
        mapRef.current.fitBounds(bounds);
        if (drivers.length === 1) {
          mapRef.current.setZoom(14);
        }
      } catch(e) {}
    }
  }, [drivers, selectedDriver, mapLoaded, onSelectDriver]);

  return (
    <div ref={mapContainerRef} className="h-[400px] w-full bg-slate-800" />
  );
};

export default ClientDashboard;
